import React, { useEffect } from "react"
import { LexicalEditor } from "lexical"
import {
  MAX_ALLOWED_FONT_SIZE,
  MIN_ALLOWED_FONT_SIZE,
  updateFontSizeInSelection,
} from "../../utils"

import "./FontSizePlugin.less"

const parseFontSize = (input: string): [number, string] | null => {
  const match = input.match(/^(\d+(?:\.\d+)?)(px|pt)$/)
  return match ? [Number(match[1]), match[2]] : null
}

const normalizeToPx = (fontSize: number, unit: string): number => {
  return unit === "pt" ? Math.round((fontSize * 4) / 3) : fontSize
}

export const parseFontSizeForToolbar = (input: string): string => {
  const parsed = parseFontSize(input)
  if (!parsed) {
    return ""
  }

  const [fontSize, unit] = parsed
  const fontSizePx = normalizeToPx(fontSize, unit)
  return `${fontSizePx}px`
}

const FontSizePlugin = ({
  selectionFontSize,
  disabled,
  editor,
}: {
  selectionFontSize: string
  disabled: boolean
  editor: LexicalEditor
}) => {
  const [inputValue, setInputValue] = React.useState<string>(selectionFontSize)
  const [inputChangeFlag, setInputChangeFlag] = React.useState<boolean>(false)
  const [isMouseMode, setIsMouseMode] = React.useState(false)

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const inputValueNumber = Number(inputValue)

    if (e.key === "Tab") {
      return
    }
    if (["e", "E", "+", "-"].includes(e.key) || isNaN(inputValueNumber)) {
      e.preventDefault()
      setInputValue("")
      return
    }
    setInputChangeFlag(true)
    if (e.key === "Enter" || e.key === "Escape") {
      e.preventDefault()

      updateFontSizeByInputValue(inputValueNumber, !isMouseMode)
    }
  }

  const handleInputBlur = () => {
    setIsMouseMode(false)

    if (inputValue !== "" && inputChangeFlag) {
      const inputValueNumber = Number(inputValue)
      updateFontSizeByInputValue(inputValueNumber)
    }
  }

  const handleClick = () => {
    setIsMouseMode(true)
  }

  const updateFontSizeByInputValue = (inputValueNumber: number, skipRefocus: boolean = false) => {
    let updatedFontSize = inputValueNumber
    if (inputValueNumber > MAX_ALLOWED_FONT_SIZE) {
      updatedFontSize = MAX_ALLOWED_FONT_SIZE
    } else if (inputValueNumber < MIN_ALLOWED_FONT_SIZE) {
      updatedFontSize = MIN_ALLOWED_FONT_SIZE
    }

    setInputValue(String(updatedFontSize))
    updateFontSizeInSelection(editor, String(updatedFontSize) + "px", null, skipRefocus)
    setInputChangeFlag(false)
  }

  useEffect(() => {
    setInputValue(selectionFontSize)
  }, [selectionFontSize])

  return (
    <>
      <button
        type="button"
        disabled={
          disabled || (selectionFontSize !== "" && Number(inputValue) <= MIN_ALLOWED_FONT_SIZE)
        }
        onClick={(e) => {
          e.preventDefault()
          updateFontSizeByInputValue(+inputValue - 2)
        }}
        className="toolbar-item font-decrement"
        aria-label="Decrease font size"
      >
        <i className="format minus-icon" />
      </button>

      <input
        type="number"
        title="Font size"
        value={inputValue}
        disabled={disabled}
        className="toolbar-item font-size-input"
        min={MIN_ALLOWED_FONT_SIZE}
        max={MAX_ALLOWED_FONT_SIZE}
        onChange={(e) => {
          e.preventDefault()
          setInputValue(e.target.value)
        }}
        onClick={handleClick}
        onKeyDown={handleKeyPress}
        onBlur={handleInputBlur}
      />

      <button
        type="button"
        disabled={
          disabled || (selectionFontSize !== "" && Number(inputValue) >= MAX_ALLOWED_FONT_SIZE)
        }
        onClick={(e) => {
          e.preventDefault()
          updateFontSizeByInputValue(+inputValue + 2)
        }}
        className="toolbar-item font-increment"
        aria-label="Increase font size"
      >
        <i className="format add-icon" />
      </button>
    </>
  )
}

export default FontSizePlugin
